import CodeEditor from "../common/code-editor";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { Button, Input, Space } from "antd";
import { DeleteOutlined } from "@ant-design/icons";

import LoadingSpinner from "../common/loading";
import DeletePremadeDialog from "./delete-premade-dialog";
import { Tag } from "antd";
import {
  buildPremade,
  deletePremade,
  formatPremade,
  getPremade,
  publishPremade,
  savePremade,
} from "./premade-service";
import { useToast } from "../common/toast";
import useApiCall from "../common/api-call";

const PremadeScriptView = ({
  premadeId: propPremadeId,
  version: propVersion,
  onNavigateBack,
  isInDialog = false,
}) => {
  const { successToast, warningToast, errorToast } = useToast();
  const history = useHistory();
  const [spinButton, setSpinButton] = useState(null);

  const [loading, setLoading] = useState(true);
  const [premade, setPremade] = useState(null);
  const [premadeId, setPremadeId] = useState(propPremadeId || null);
  const [version, setVersion] = useState(propVersion || "");
  const [code, setCode] = useState("");
  const [summary, setSummary] = useState("");
  const [parseResults, setParseResults] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const { apiCall } = useApiCall();

  useEffect(() => {
    if (propPremadeId && propVersion) {
      // If props are provided (dialog mode), use them
      loadPremade({ id: propPremadeId, version: propVersion });
    } else {
      // Otherwise, read from URL parameters (standalone mode)
      const searchParams = new URLSearchParams(window.location.search);
      const params = {};
      for (const [key, value] of searchParams.entries()) {
        params[key] = value;
      }
      if (params["id"]) {
        loadPremade(params);
      } else {
        setPremade({ version: "draft" });
        setVersion("draft");
        setLoading(false);
      }
    }
  }, [propPremadeId, propVersion]);

  const loadPremade = async params => {
    setLoading(true);
    const premade = await getPremade(apiCall, params["id"], params["version"]);
    if (premade && !premade.error) {
      setPremade(premade);
      setPremadeId(premade.premadeId);
      setVersion(premade.version);
      setCode(premade.script);
      setParseResults(premade.parseResults);
      setSummary(premade.summary);
      if (!isInDialog) {
        history.replace(
          `/premade/script?id=${premade.premadeId}&version=${premade.version}`,
        );
      }
    } else {
      setPremade({ version: "draft" });
      errorToast("Error!");
    }
    setLoading(false);
  };

  const handleSave = async () => {
    // Validation
    if (!summary.trim()) {
      errorToast("Summary is required!");
      return;
    }
    if (!code.trim()) {
      errorToast("Script is required!");
      return;
    }

    setSpinButton("save");

    const resp = await savePremade(apiCall, premadeId, code, summary);
    if (resp != null && !resp.error) {
      successToast("Saved!");
      loadPremade({ id: resp.premadeId, version: resp.version });
    } else {
      errorToast("Error!");
    }
    setSpinButton(null);
  };

  const handlePublish = async () => {
    setSpinButton("publish");
    const resp = await publishPremade(apiCall, premadeId);
    if (resp) {
      successToast("Published!");
      await loadPremade({ id: premadeId, version: "prod" });
    } else {
      errorToast("Publish Failed!");
    }
    setSpinButton(null);
  };

  const handleBuild = async () => {
    if (!code.trim()) {
      errorToast("Script is required for build!");
      return;
    }

    const resp = await buildPremade(apiCall, code);
    if (resp && !resp.error) {
      setParseResults(resp.parseResults);
      if (resp.parseResults.length > 0) {
        warningToast("Build Issues Found");
      } else {
        successToast("Build Successful");
      }
    } else {
      errorToast("Build Error!");
    }
  };

  const handleFormat = async () => {
    if (!code.trim()) {
      errorToast("Script is required for format!");
      return;
    }

    const resp = await formatPremade(apiCall, code);
    if (resp) {
      setParseResults(resp.parseResults);
      if (resp.parseResults.length === 0) {
        successToast("Format Successful");
        setCode(resp.script);
      } else {
        warningToast("Format Issues Found");
      }
    } else {
      errorToast("Format Error!");
    }
  };

  const handleDelete = async () => {
    const success = await deletePremade(apiCall, premadeId, version);
    if (success) {
      if (isInDialog && onNavigateBack) {
        onNavigateBack();
      } else {
        history.replace("/premade/manage");
      }
    } else {
      errorToast("Error. Please retry.");
    }
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div
          style={{
            flexGrow: 1,
            height: isInDialog ? "calc(90vh - 120px)" : "calc(100vh - 64px)",
            padding: 16,
          }}
        >
          <Space
            direction="vertical"
            size="middle"
            style={{ width: "100%", height: "100%" }}
          >
            {/* Action buttons bar */}
            <Space
              wrap
              style={{ width: "100%" }}
            >
              <Tag
                color={
                  version === "draft"
                    ? "orange"
                    : version === "stg"
                    ? "blue"
                    : "green"
                }
              >
                {version}
              </Tag>
              <Button type="default" onClick={handleBuild}>
                Build
              </Button>
              <Button type="default" onClick={handleFormat}>
                Format
              </Button>
              <Button
                type="primary"
                onClick={handleSave}
                disabled={spinButton !== null}
                loading={spinButton === "save"}
              >
                Save
              </Button>
              {premade.version === "draft" ||
                (premade.version === "stg" && (
                  <Button
                    type="primary"
                    onClick={handlePublish}
                    disabled={
                      (version !== "draft" && version !== "stg") ||
                      !premadeId ||
                      spinButton !== null
                    }
                    loading={spinButton === "publish"}
                  >
                    Publish
                  </Button>
                ))}
              {premade.version !== "draft" && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteConfirm(true)}
                  aria-label="delete"
                />
              )}
            </Space>

            {/* Summary input */}
            <div>
              <Input.TextArea
                placeholder="Enter a brief description of this premade..."
                value={summary}
                onChange={e => {
                  setSummary(e.target.value);
                }}
                rows={2}
                style={{ width: "100%" }}
              />
            </div>

            {/* Code editor */}
            <div style={{ flex: 1, height: "100%" }}>
              <CodeEditor
                value={code}
                editable={true}
                parseResults={parseResults}
                height={
                  isInDialog ? "calc(90vh - 320px)" : "calc(100vh - 200px)"
                }
                onChange={instance => {
                  const code = instance.getValue();
                  setCode(code);
                }}
              />
            </div>
          </Space>

          <DeletePremadeDialog
            open={deleteConfirm}
            onClose={() => setDeleteConfirm(false)}
            onDelete={handleDelete}
          ></DeletePremadeDialog>
        </div>
      )}
    </>
  );
};

export default PremadeScriptView;
